import { authService } from './auth'
import { safeJsonParse } from '../utils/json-bigint'

// 创建带拦截器的fetch函数
const createFetchWithInterceptor = () => {
  return async (url: string, options: RequestInit = {}) => {
    // 添加token到请求头
    const token = authService.getToken()
    if (token) {
      const headers: Record<string, string> = {
        ...options.headers as Record<string, string>,
        'Authorization': `Bearer ${token}`,
      }
      
      // 只有在不是FormData的情况下才设置Content-Type
      if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json'
      }
      
      options.headers = headers
    }

    try {
      const response = await fetch(url, options)
      
      // 检查响应状态
      if (response.status === 401) {
        // Token失效，清除本地存储并跳转到登录页
        authService.clearToken()
        // 避免静态导入 router 造成循环依赖，直接跳转
        const base = import.meta.env.BASE_URL || '/'
        window.location.replace(base)
        throw new Error('登录已过期，请重新登录')
      }

      if (response.status === 403) {
        throw new Error('没有权限访问该资源')
      }

      if (!response.ok) {
        throw new Error(`请求失败: ${response.status}`)
      }

      return response
    } catch (error) {
      if (error instanceof Error && error.message.includes('登录已过期')) {
        throw error
      }
      
      // 网络错误或其他错误
      throw new Error('网络错误，请稍后重试')
    }
  }
}

// 导出带拦截器的fetch
export const fetchWithInterceptor = createFetchWithInterceptor()

// 通用API请求函数
export const apiRequest = async <T = any>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await fetchWithInterceptor(url, options)
  // 使用安全的 JSON 解析，避免大整数精度丢失
  const data = await safeJsonParse(response)
  
  // 检查业务状态码
  if (data.code !== 200) {
    // 根据不同的错误码提供更详细的错误信息
    let errorMessage = data.msg || '请求失败'
    
    // 特殊错误码处理
    switch (data.code) {
      case 404:
        errorMessage = '请求的资源不存在'
        break
      case 500:
        errorMessage = '服务器内部错误，请稍后重试'
        break
      case 403:
        errorMessage = '没有权限访问该资源'
        break
      case 401:
        errorMessage = '登录已过期，请重新登录'
        break
      default:
        // 保持原始错误信息
        break
    }
    
    throw new Error(errorMessage)
  }
  
  return data
}

// 导出原有的函数（保持兼容性）
export const sleep = (ms = 0) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export const validators = {
  email: (v: string) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return pattern.test(v) || 'Please enter a valid email address'
  },
  required: (v: any) => !!v || 'This field is required',
}

// API基础配置 - 在开发环境中使用代理路径，生产环境使用实际API URL
export const API_BASE_URL = import.meta.env.DEV ? '/api' : (import.meta.env.VITE_API_BASE_URL || 'https://sdk.xmuse.cn/api')

// API端点配置
export const API_ENDPOINTS = {
  // 用户相关
  users: {
    list: () => `${API_BASE_URL}/api/users`,
    detail: (id: string | number) => `${API_BASE_URL}/api/users/${id}`,
    avatars: () => `${API_BASE_URL}/api/avatars`,
    login: () => `${API_BASE_URL}/user/login`,
    register: () => `${API_BASE_URL}/user/register`,
    resetPassword: () => `${API_BASE_URL}/user/reset/password`,
    getCode: (phoneOrEmail: string) => `${API_BASE_URL}/user/get/code?phoneOrEmail=${encodeURIComponent(phoneOrEmail)}`,
    current: () => `${API_BASE_URL}/user/get/current`,
    adminPage: () => `${API_BASE_URL}/user/admin/page`,
    bindPhone: () => `${API_BASE_URL}/user/update/phone`,
    bindEmail: () => `${API_BASE_URL}/user/update/email`,
    updateUsername: () => `${API_BASE_URL}/user/update/username`,
    // 系统管理用户相关
    systemPage: () => `${API_BASE_URL}/user/system/page`,
    systemAdminCreate: () => `${API_BASE_URL}/user/system/admin/create`,
    systemDelete: (userId: string | number) => `${API_BASE_URL}/user/system/delete/${userId}`,
    adminUpdate: () => `${API_BASE_URL}/user/admin/update`,
    systemUpdate: () => `${API_BASE_URL}/user/system/update`,
    systemUpdateSubscription: (userId: string | number) => `${API_BASE_URL}/user/system/update/${userId}/subscription`,
    adminCreate: () => `${API_BASE_URL}/user/admin/create`,
    adminDelete: (userId: string | number) => `${API_BASE_URL}/user/admin/delete/${userId}`,
  },
  
  // 应用相关
  apps: {
    list: () => `${API_BASE_URL}/app/list`,
    create: () => `${API_BASE_URL}/app/create`,
    delete: (id: string | number) => `${API_BASE_URL}/app/delete/${id}`,
    adminDelete: (id: string | number) => `${API_BASE_URL}/app/admin/delete/${id}`,
    adminPage: () => `${API_BASE_URL}/app/admin/page`,
    adminUpdateStatus: (id: string | number) => `${API_BASE_URL}/app/admin/status/${id}`,
  },
  
  // 模块相关
  modules: {
    list: () => `${API_BASE_URL}/module/list`,
    interfaces: (moduleId: string | number) => `${API_BASE_URL}/module/get/${moduleId}/interfaces`,
    create: () => `${API_BASE_URL}/module/admin/create`,
    delete: (moduleId: string | number) => `${API_BASE_URL}/module/admin/delete/${moduleId}`,
  },
  
  // 版本相关
  versions: {
    list: () => `${API_BASE_URL}/version/list`,
    current: () => `${API_BASE_URL}/version/list/current`,
    create: () => `${API_BASE_URL}/version/create`,
    update: () => `${API_BASE_URL}/version/update`,
    delete: (versionId: string | number) => `${API_BASE_URL}/version/delete/${versionId}`,
  },
  
  // 下载相关
  download: {
    get: (appId: string | number) => `${API_BASE_URL}/download/get?appId=${appId}`,
    adminDelete: (appId: string | number) => `${API_BASE_URL}/download/admin/delete?appId=${appId}`,
  },
  
  // 仪表盘相关
  dashboard: {
    get: () => `${API_BASE_URL}/dashboard/get`,
    // 管理员与超级管理员共用
    adminGet: () => `${API_BASE_URL}/dashboard/admin/get`,
  },

  // 功能管理相关
  features: {
    page: () => `${API_BASE_URL}/feature/admin/page`,
    updateRecord: (interfaceId: string | number) => `${API_BASE_URL}/feature/admin/update/${interfaceId}`,
    batchUpdateRecord: () => `${API_BASE_URL}/feature/admin/update/batch`,
    updateModuleRecord: (moduleId: string | number) => `${API_BASE_URL}/feature/admin/update/module/${moduleId}`,
    // 新增的接口状态管理
    updateStatus: (interfaceId: string | number) => `${API_BASE_URL}/feature/admin/status/${interfaceId}`,
    batchUpdateStatus: () => `${API_BASE_URL}/feature/admin/status/batch`,
    // 新增的接口模块管理
    updateModule: (interfaceId: string | number) => `${API_BASE_URL}/feature/admin/module/${interfaceId}`,
    // 新增的模块付费等级管理
    updatePaymentLevel: (moduleId: string | number) => `${API_BASE_URL}/feature/admin/payment/${moduleId}`,
  },

  // 服务接口相关
  services: {
    list: () => `${API_BASE_URL}/service/list`,
    page: () => `${API_BASE_URL}/service/admin/page`,
    create: () => `${API_BASE_URL}/service/admin/create`,
    update: () => `${API_BASE_URL}/service/admin/update`,
    delete: (id: string | number) => `${API_BASE_URL}/service/admin/delete/${id}`,
    get: (id: string | number) => `${API_BASE_URL}/service/get?interfaceId=${id}`,
  },
  
  // 订单相关
  orders: {
    create: () => `${API_BASE_URL}/order/create`,
    pay: () => `${API_BASE_URL}/order/pay`,
    close: (orderNo: string) => `${API_BASE_URL}/order/close?orderNo=${encodeURIComponent(orderNo)}`,
    get: (orderNo: string) => `${API_BASE_URL}/order/get?orderNo=${encodeURIComponent(orderNo)}`,
    page: () => `${API_BASE_URL}/order/page`,
    // 管理员订单相关
    adminPage: () => `${API_BASE_URL}/order/admin/page`,
    adminGet: (orderNo: string) => `${API_BASE_URL}/order/admin/get?orderNo=${encodeURIComponent(orderNo)}`,
    adminPageRefund: () => `${API_BASE_URL}/order/admin/page/refund`,
    preRefund: () => `${API_BASE_URL}/order/pre-refund`,
    adminRefund: (refundNo: string) => `${API_BASE_URL}/order/admin/refund?refundNo=${encodeURIComponent(refundNo)}`,
    adminUpdateRefund: () => `${API_BASE_URL}/order/admin/update/refund`,
    // 异常订单相关
    adminPageException: () => `${API_BASE_URL}/order/admin/page/exception`,
    adminProcessException: (exceptionId: number) => `${API_BASE_URL}/order/admin/process/exception?exceptionId=${exceptionId}`,
    adminBatchProcessException: () => `${API_BASE_URL}/order/admin/batch/process/exception`,
    adminExceptionStatistics: () => `${API_BASE_URL}/order/admin/exception/statistics`,
    adminReconcile: () => `${API_BASE_URL}/order/admin/reconcile`,
    adminExceptionComparison: (exceptionId: number) => `${API_BASE_URL}/order/admin/exception/comparison?exceptionId=${exceptionId}`,
    adminProcessExceptionV2: () => `${API_BASE_URL}/order/admin/process/exception/v2`,
    adminIgnoreException: (exceptionId: number) => `${API_BASE_URL}/order/admin/ignore/exception?exceptionId=${exceptionId}`,
  },
  
  // 套餐相关
  plans: {
    list: () => `${API_BASE_URL}/plan/list`,
    get: (planId: string | number) => `${API_BASE_URL}/plan/get?planId=${planId}`,
    systemUpdate: () => `${API_BASE_URL}/plan/system/update`,
  },
  
  // 配置管理相关
  config: {
    getCurrent: () => `${API_BASE_URL}/config/admin/get/current`,
    update: () => `${API_BASE_URL}/config/admin/update`,
  },
  
  // 日志管理相关
  logs: {
    page: () => `${API_BASE_URL}/log/page`,
  },
}

// 默认导出，保持兼容性
export default {
  baseUrl: API_BASE_URL,
  fetchWithInterceptor,
  apiRequest,
  sleep,
  validators,
  endpoints: API_ENDPOINTS,
  // 保持原有的api路径函数
  allUsers: () => API_ENDPOINTS.users.list(),
  user: (id: string | number) => API_ENDPOINTS.users.detail(id),
  avatars: () => API_ENDPOINTS.users.avatars(),
}
